import { useState, useCallback } from 'react';
import { getLastDateRange } from '../../../utils/storage';
import { getDateRangeFilter, parseCustomUtcRange } from '../../../utils/dateUtils';
import { DateRange, PRESET_DATE_RANGES } from '../../../constants/ui';
import { SortState } from '../types';

export interface UseURLStateReturn {
  filter: string;
  setFilter: React.Dispatch<React.SetStateAction<string>>;
  sort: SortState;
  setSort: React.Dispatch<React.SetStateAction<SortState>>;
  dateRange: DateRange;
  setDateRange: React.Dispatch<React.SetStateAction<DateRange>>;
  customDateRange: { startDate: Date; endDate: Date } | null;
  setCustomDateRange: React.Dispatch<React.SetStateAction<{ startDate: Date; endDate: Date } | null>>;
  selectedWorkspace: string;
  setSelectedWorkspace: React.Dispatch<React.SetStateAction<string>>;
  selectedModel: string;
  setSelectedModel: React.Dispatch<React.SetStateAction<string>>;
  getDateRange: () => { startDate: Date; endDate: Date; label: string };
  updateURL: (filter: string, sort: SortState, dateRange?: DateRange, customDateRange?: { startDate: Date; endDate: Date } | null, workspace?: string, model?: string) => void;
}

function getInitialStateFromURL() {
  const params = new URLSearchParams(window.location.search);
  const dateRangeParam = params.get('dateRange');
  const startDateParam = params.get('startDate');
  const endDateParam = params.get('endDate');

  // Get saved preferences from local storage
  const savedPreferences = getLastDateRange();

  let initialDateRange: DateRange = '1day';
  let initialCustomDateRange: { startDate: Date; endDate: Date } | null = null;

  // Priority: URL parameters > Local storage > Default
  if (startDateParam && endDateParam) {
    initialCustomDateRange = parseCustomUtcRange(startDateParam, endDateParam);
  } else if (dateRangeParam && PRESET_DATE_RANGES.includes(dateRangeParam as DateRange)) {
    initialDateRange = dateRangeParam as DateRange;
  } else if (savedPreferences.dateRange === 'custom' && savedPreferences.customDateRange) {
    initialCustomDateRange = parseCustomUtcRange(savedPreferences.customDateRange.startDate, savedPreferences.customDateRange.endDate);
  } else if (savedPreferences.dateRange && PRESET_DATE_RANGES.includes(savedPreferences.dateRange as DateRange)) {
    initialDateRange = savedPreferences.dateRange as DateRange;
  }

  return {
    filter: params.get('filter') || '',
    sort: {
      key: params.get('sortBy') || null,
      dir: (params.get('sortDir') as 'asc' | 'desc') || 'asc'
    } as SortState,
    dateRange: initialDateRange,
    customDateRange: initialCustomDateRange,
    workspace: params.get('workspace') || 'all',
    model: params.get('model') || 'all'
  };
}

export function useURLState(): UseURLStateReturn {
  const initialState = getInitialStateFromURL();

  const [filter, setFilter] = useState(initialState.filter);
  const [sort, setSort] = useState<SortState>(initialState.sort);
  const [dateRange, setDateRange] = useState<DateRange>(initialState.dateRange);
  const [customDateRange, setCustomDateRange] = useState<{ startDate: Date; endDate: Date } | null>(initialState.customDateRange);
  const [selectedWorkspace, setSelectedWorkspace] = useState<string>(initialState.workspace);
  const [selectedModel, setSelectedModel] = useState<string>(initialState.model);

  // Get date range helper that supports custom dates
  const getDateRangeFn = useCallback(() => {
    if (customDateRange) {
      return {
        startDate: customDateRange.startDate,
        endDate: customDateRange.endDate,
        label: `${customDateRange.startDate.toISOString().split('T')[0]} ~ ${customDateRange.endDate.toISOString().split('T')[0]}`
      };
    }
    return getDateRangeFilter(dateRange);
  }, [dateRange, customDateRange]);

  // Function to update URL parameters
  const updateURL = useCallback((newFilter: string, newSort: SortState, newDateRange?: DateRange, newCustomDateRange?: { startDate: Date; endDate: Date } | null, newWorkspace?: string, newModel?: string) => {
    const params = new URLSearchParams();

    if (newFilter) {
      params.set('filter', newFilter);
    }
    if (newSort.key) {
      params.set('sortBy', newSort.key);
      params.set('sortDir', newSort.dir);
    }

    if (newCustomDateRange) {
      params.set('startDate', newCustomDateRange.startDate.toISOString().split('T')[0]);
      params.set('endDate', newCustomDateRange.endDate.toISOString().split('T')[0]);
    } else if (newDateRange) {
      params.set('dateRange', newDateRange);
    }

    if (newWorkspace && newWorkspace !== 'all') {
      params.set('workspace', newWorkspace);
    }

    if (newModel && newModel !== 'all') {
      params.set('model', newModel);
    }

    const newURL = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
    window.history.replaceState({}, '', newURL);
  }, []);

  return {
    filter,
    setFilter,
    sort,
    setSort,
    dateRange,
    setDateRange,
    customDateRange,
    setCustomDateRange,
    selectedWorkspace,
    setSelectedWorkspace,
    selectedModel,
    setSelectedModel,
    getDateRange: getDateRangeFn,
    updateURL,
  };
}
